<template>
    <div class="flex w-full justify-center items-center h-screen">
        <div class="container w-[28rem] bg-[#f8f4f3] p-8 rounded-md">
            <div class="flex flex-col items-center pb-4 mb-4">
                <img src="/icon-512x512.png" alt="Vue Logo" />
                <span>期限管理システム</span>
                <span>リミトック</span>
            </div>

            <el-form 
                :model="form" 
                :rules="rules"
                ref="formRef"
                label-width="auto">

                <el-form-item prop="email">
                    <el-input v-model="form.email" placeholder="メールアドレス" @keyup.enter="onSubmit" />
                </el-form-item>

                <el-form-item prop="password">
                    <el-input show-password v-model="form.password" placeholder="パスワード" @keyup.enter="onSubmit" />
                </el-form-item>

                <div class="mb-6">
                    <p v-if="errorMessage" class="text-red-500  text-xs">{{ errorMessage }}</p>
                </div>
                <div class="flex justify-center">
                    <el-button type="primary" class="w-full" @click="onSubmit">ログイン</el-button>
                </div>
                <div v-if="isAppleDevice" class="flex justify-center">
                    <el-button type="primary" class="mt-4 w-full" @click="requestNotificationPermission">
                        通知の許可を要求（Appleデバイスのみ）
                    </el-button>
                </div>
            </el-form>

        </div>
    </div>
</template>

<script lang="ts">
import { defineComponent, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import axios from 'axios';
import { ElMessage, ElNotification } from 'element-plus';
import { authService } from '@/services/authService';
import { ROUTES } from '@/config/constants';
import type { FormInstance } from 'element-plus';

export default defineComponent({
    name: 'Login',

    setup() {
        const formRef = ref<FormInstance>();
        const form = ref({
            email: '',
            password: '',
        });
        
        const rules = {
            email: [
                { required: true, message: 'メールアドレスを入力してください', trigger: 'blur' }
            ],
            password: [
                { required: true, message: 'パスワードを入力してください', trigger: 'blur' }
            ]
        };

        const errorMessage = ref('');
        const router = useRouter();
        const isAppleDevice = ref(false);

        // Appleデバイスかどうかを判定する関数
        const checkIfAppleDevice = () => {
            const userAgent = navigator.userAgent.toLowerCase();
            return /iphone|ipad|mac/.test(userAgent);
        };

        // コンポーネントのマウント時にデバイスをチェック
        onMounted(async () => {
            isAppleDevice.value = checkIfAppleDevice();
            await requestNotificationPermission();
        });

        const onSubmit = async () => {
            if (!formRef.value) return;
            
            await formRef.value.validate(async (valid) => {
                if (valid) {
                    try {
                        const response = await axios.post('/api/login', {
                            email: form.value.email,
                            password: form.value.password
                        });

                        if (response.data.message === 'ログインに成功しました') {
                            // ==================== [Change Block Start] 可回退: 登录返回字段新增组织开关 ====================
                            // 修改前:
                            // const { token, user_id, user_name, role, organization_id, organization_name, department_id, department_name, is_head, is_pay_must } = response.data;
                            const { token, user_id, user_name, role, organization_id, organization_name, department_id, department_name, is_head, is_pay_must, is_sub_pay_to_head_enabled } = response.data;

                            localStorage.setItem('token', token);
                            localStorage.setItem('role', role);
                            localStorage.setItem('user_id', user_id);
                            localStorage.setItem('user', user_name);
                            localStorage.setItem('organization_id', organization_id);
                            localStorage.setItem('organization_name', organization_name);
                            localStorage.setItem('department_id', department_id);
                            localStorage.setItem('department_name', department_name);
                            localStorage.setItem('is_head', is_head);
                            localStorage.setItem('is_pay_must', is_pay_must);
                            // 修改前:
                            // (无 is_sub_pay_to_head_enabled 本地缓存)
                            localStorage.setItem('is_sub_pay_to_head_enabled', is_sub_pay_to_head_enabled);
                            // ==================== [Change Block End] 可回退 ====================
                            router.push(`/${ROUTES.ALARM}`)
                            ElMessage.info(`ログインしました！ログインの有効時間は2時間です。`);
                            // 异常処理
                            // TODO: 暂时关闭
                            authService.handlePostLoginTasks(user_id);
                        } else {
                            errorMessage.value = 'ユーザー名またはパスワードが間違っています。';
                        }
                    } catch (error) {
                        errorMessage.value = 'ユーザー名またはパスワードが間違っています。';
                    }
                }
            });
        };

        // 请求通知权限
        const requestNotificationPermission = async () => {
            try {
                const permission = await Notification.requestPermission();
                if (permission === 'granted') {

                } else {
                    console.error('Notification permission denied.')
                    ElNotification({
                        title: 'メッセージ',
                        message: '通知を受け取るには、通知の許可をしてください。',
                        type: 'info',
                    });
                }
            } catch (error) {
                console.error('Error getting FCM token:', error);
            }
        };

        return {
            formRef,
            form,
            rules,
            errorMessage,
            onSubmit,
            requestNotificationPermission,
            isAppleDevice
        };
    }
});
</script>
